import { FontsModel } from "@store/Components/Common/Fonts";
import { PATH } from "@store/constants";
import parseNumber from "@store/utilities/parseNumber";

// -- Colors

const swatch = {
    black: "#000000",
    white: "#ffffff",
    grey: {
        lighter: "#FCFCFC",
        light: "#F6F6F6",
        mid: "#ECECEC",
        main: "#9CA5B2",
        dark: "#36404C",
    },
};

export const COLORS = {
    black: swatch.black,
    white: swatch.white,
    primary: {
        light: "#71BBC1",
        base: "#00838F",
        dark: "#0F636D",
    },
    secondary: {
        light: "#A7AEB6",
        base: "#4A4F54",
        dark: "#2A2D30",
    },
    tertiary: {
        light: "#5793EB",
        base: "#031734",
        dark: "#010C1C",
    },
    invert: {
        base: swatch.white,
        dark: swatch.grey.mid,
    },
    success: {
        light: "#DEF1F2",
        base: "#007278",
    },
    error: {
        light: "#FEE1DE",
        base: "#AC223A",
    },
    warning: {
        light: "#FFF4DE",
        base: "#E5AF48",
    },
    info: {
        light: "#E3EAF4",
        base: "#476591",
    },
    background: {
        base: swatch.white,
        alt: swatch.grey.light,
        altLight: swatch.grey.lighter,
    },
    border: {
        base: "#E9E9E9",
        dark: "#E6E6E6",
    },
    disabled: {
        lighter: swatch.grey.light,
        light: swatch.grey.mid,
        base: swatch.grey.main,
        dark: swatch.grey.dark,
    },
    text: {
        disabled: "#939393",
        semi: "#666666",
        base: "#4A4F54",
        accent: "#36404C",
    },
    link: {
        base: "#00838F",
        hover: "#0F636D",
    },
    shadow: {
        light: "rgba(48, 49, 51, 0.1)",
        lighter: "rgba(48, 49, 51, 0.05)",
        dark: "rgba(13, 13, 13, 0.9)",
        darker: "rgba(13, 13, 13)",
    },
    pagination: {
        base: "#00838F",
        hover: "#0F636D",
    },
    footer: {
        base: swatch.grey.light,
        dark: swatch.grey.mid,
    },
    outline: "#9CA5B2",
};

// -- Typography

const fallbackFont = "Arial, Helvetica, sans-serif";

export const TYPOGRAPHY = {
    fontFamily: {
        title: `'Abrade', ${fallbackFont}`,
        base: `'Abrade', ${fallbackFont}`,
    },
    weight: {
        light: 300,
        base: 400,
        bold: 700,
        black: 900,
    },
    size: {
        veryTiny: "10px",
        tiny: "12px",
        smallest: "13px",
        smaller: "14px",
        small: "15px",
        base: "16px",
        large: "18px",
        larger: "21px",
        largest: "25px",
    },
    lineHeight: {
        smaller: 1,
        small: 1.25,
        base: 1.5,
        large: 2,
    },
    letterSpacing: {
        larger: "1px",
        large: "0.5px",
        base: "0",
    },
    spacing: {
        small: "0.5em",
        base: "1em",
        large: "1.5em",
        larger: "2em",
        largest: "3em",
    },
};

// -- Fonts

export const FONTS: FontsModel = [
    {
        family: "Abrade",
        url: "//hello.myfonts.net/count/3d98d5",
        files: [
            { name: "AbradeLight", weight: TYPOGRAPHY.weight.light, style: "normal" },
            { name: "AbradeLightItalic", weight: TYPOGRAPHY.weight.light, style: "italic" },
            { name: "AbradeBook", weight: TYPOGRAPHY.weight.base, style: "normal" },
            { name: "AbradeBookItalic", weight: TYPOGRAPHY.weight.base, style: "italic" },
            { name: "AbradeMedium", weight: TYPOGRAPHY.weight.bold, style: "normal" },
            { name: "AbradeMediumItalic", weight: TYPOGRAPHY.weight.bold, style: "italic" },
            { name: "AbradeBold", weight: TYPOGRAPHY.weight.black, style: "normal" },
            { name: "AbradeBoldItalic", weight: TYPOGRAPHY.weight.black, style: "italic" },
        ],
    },
];

// -- Product

const productImage = {
    small: {
        width: 210,
        height: 210,
        defaultPath: `${PATH.staticFiles}/product-unavailable-small.png`,
    },
    medium: {
        width: 768,
        height: 768,
        defaultPath: `${PATH.staticFiles}/product-unavailable-medium.png`,
    },
    large: {
        width: 1200,
        height: 1200,
        defaultPath: `${PATH.staticFiles}/product-unavailable-large.png`,
    },
};

export const PRODUCT = {
    image: {
        ...productImage,
        ratio: productImage.large.height / productImage.large.width,
    },
};

// -- Category

const categoryImage = {
    small: {
        width: 768,
        height: 768,
        defaultPath: `${PATH.staticFiles}/category-unavailable.png`,
    },
    large: {
        width: 975,
        height: 550,
    },
};

export const CATEGORY = {
    image: {
        small: {
            ...categoryImage.small,
            ratio: categoryImage.small.height / categoryImage.small.width,
        },
        large: {
            ...categoryImage.large,
            ratio: categoryImage.large.height / categoryImage.large.width,
        },
    },
};

// -- Common

const contentWidth = 1410;
const spacing = {
    side: ["20px", "30px", "45px", "45px", "60px"],
    content: ["25px", "30px", "45px", "45px", "60px"],
    narrowContent: ["25px", "30px", "40px", "40px", "50px"],
    spacedContent: ["50px", "60px", "90px", "90px", "120px"],
};

const largestSideSpacing = spacing.side.filter(x => x).pop() as any;
const siteWidth = contentWidth + (parseNumber(largestSideSpacing) ?? 0) * 2;

const contentMaxWidths = [...spacing.side].map(x => (x ? `calc(100vw - (${x} * 2))` : null));
contentMaxWidths.splice(-1, 1, `${contentWidth}px`);

export const COMMON = {
    breakpoints: [0, 600, 900, 1200, siteWidth],
    contentMaxWidths,
    width: {
        content: contentWidth,
        site: siteWidth,
    },
    spacing,
    outline: {
        width: "1px",
        style: "solid",
    },
};

// -- Menu

export const MENU = {
    mobileButtonSpacing: ".5em",
};

// -- Form

export const FORM = {
    field: {
        padding: "1em",
        spacing: "1em",
    },
    button: {
        spacing: "1.6em",
    },
};

// -- Product list

export const PRODUCT_LIST = {
    headingHeight: "65px",
    gridHorizontalGap: ["30px"],
    gridVerticalGap: ["70px", null, null, "80px"],
};

// -- Lists list

export const LISTS_LIST = {
    gridHorizontalGap: ["40px"],
    gridVerticalGap: ["40px"],
};
